"use client"

import type React from "react"
import { createContext, useContext, useState, useEffect, useCallback } from "react"
import { useQuery } from "@tanstack/react-query"
import { listConversations } from "@edemics/services/communication/chatService"

/**
 * How often the chat unread poll fires while the tab is focused.
 * 30s is the "sensible polling" the platform standardised on — quick
 * enough that a new message shows up before the user checks manually,
 * slow enough that idle tabs don't hammer the API.
 */
const CHAT_POLL_INTERVAL_MS = 30_000

interface ChatContextType {
  isChatPopupOpen: boolean
  openChatPopup: () => void
  closeChatPopup: () => void
  toggleChatPopup: () => void
  isLoggedIn: boolean
  /**
   * True iff the logged-in user is a tenant user (kind==='tenant').
   * Header + other consumers use this to skip tenant-scoped polls
   * (chat conversations, notifications) when super-admin is signed
   * in — those endpoints don't exist on the platform side.
   */
  isTenantUser: boolean
  unreadMessages: number
  setUnreadMessages: React.Dispatch<React.SetStateAction<number>>
  // Background/opacity state shared across ChatPopup and the in-page ChatPanel
  // so they stay in sync. See ChatPanel for consumption details.
  chatBackground: string
  setChatBackground: React.Dispatch<React.SetStateAction<string>>
  customChatBackground: string | null
  setCustomChatBackground: React.Dispatch<React.SetStateAction<string | null>>
  customChatBackgroundOpacity: number
  setCustomChatBackgroundOpacity: React.Dispatch<React.SetStateAction<number>>
}

const ChatContext = createContext<ChatContextType | undefined>(undefined)

export function ChatProvider({ children }: { children: React.ReactNode }) {
  const [isChatPopupOpen, setIsChatPopupOpen] = useState(false)
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [isTenantUser, setIsTenantUser] = useState(false)
  const [isClient, setIsClient] = useState(false)
  // unreadMessages starts at 0 and is driven by the polling query below
  // (see useUnreadMessagesFromPoll). setUnreadMessages is still exposed
  // as an override for callers that want to zero it out immediately
  // after opening a conversation (before the next poll tick).
  const [unreadMessages, setUnreadMessages] = useState(0)
  // Chat background state hoisted from ChatPanel/ChatPopup so the two
  // views stay in sync. Default is the built-in "default" theme.
  const [chatBackground, setChatBackground] = useState<string>("default")
  const [customChatBackground, setCustomChatBackground] = useState<string | null>(null)
  const [customChatBackgroundOpacity, setCustomChatBackgroundOpacity] = useState<number>(100)

  useEffect(() => {
    setIsClient(true)
  }, [])

  const checkLoginStatus = useCallback(() => {
    if (isClient) {
      const userRole = sessionStorage.getItem("userRole")
      if (userRole) {
        setIsLoggedIn(true)
        // Also read the stored user object so we can distinguish tenant
        // vs platform (super-admin) sessions. Super-admin doesn't have
        // tenant-scoped conversations, so the poll must not fire for
        // that audience.
        try {
          const raw = sessionStorage.getItem("user")
          const parsed = raw ? JSON.parse(raw) : null
          const kind = parsed?.kind ?? (parsed?.tenantId ? "tenant" : "platform")
          setIsTenantUser(kind === "tenant")
        } catch {
          setIsTenantUser(false)
        }
      } else {
        setIsLoggedIn(false)
        setIsTenantUser(false)
        setIsChatPopupOpen(false) // Ensure chat is closed if logged out
      }
    }
  }, [isClient])

  useEffect(() => {
    checkLoginStatus()
  }, [checkLoginStatus])

  // ---- Chat unread polling ------------------------------------------------
  // Only enabled once the user is logged in — no point hammering the API on
  // the login / auth screens. TanStack Query handles the interval,
  // deduplication, retry, and pause-when-tab-hidden semantics for us.
  const { data: conversations } = useQuery({
    queryKey: ["chat", "conversations", "unread-poll"],
    queryFn: listConversations,
    // Tenant-only: super-admin has no tenant-scoped conversations,
    // so the poll must not fire there (would 404/403 every 30s).
    enabled: isLoggedIn && isTenantUser,
    refetchInterval: CHAT_POLL_INTERVAL_MS,
    refetchIntervalInBackground: false,
    staleTime: CHAT_POLL_INTERVAL_MS - 1_000,
    // Silently drop errors — a poll failure isn't fatal, the next tick tries
    // again. The badge just stays where it was.
    retry: 1,
  })

  useEffect(() => {
    if (!Array.isArray(conversations)) return
    const total = conversations.reduce(
      (sum, c: any) => sum + (typeof c?.unreadCount === "number" ? c.unreadCount : 0),
      0,
    )
    setUnreadMessages(total)
  }, [conversations])

  // Listen for storage changes to handle login/logout from other tabs or direct localStorage manipulation
  useEffect(() => {
    if (!isClient) return

    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === "userRole") {
        checkLoginStatus()
      }
    }

    window.addEventListener("storage", handleStorageChange)
    // Also check on focus in case localStorage was changed while tab was inactive
    window.addEventListener("focus", checkLoginStatus)

    return () => {
      window.removeEventListener("storage", handleStorageChange)
      window.removeEventListener("focus", checkLoginStatus)
    }
  }, [isClient, checkLoginStatus])

  const openChatPopup = useCallback(() => setIsChatPopupOpen(true), [])
  const closeChatPopup = useCallback(() => setIsChatPopupOpen(false), [])
  const toggleChatPopup = useCallback(() => setIsChatPopupOpen((prev) => !prev), [])

  return (
    <ChatContext.Provider
      value={{
        isChatPopupOpen,
        openChatPopup,
        closeChatPopup,
        toggleChatPopup,
        isLoggedIn,
        isTenantUser,
        unreadMessages,
        setUnreadMessages,
        chatBackground,
        setChatBackground,
        customChatBackground,
        setCustomChatBackground,
        customChatBackgroundOpacity,
        setCustomChatBackgroundOpacity,
      }}
    >
      {children}
    </ChatContext.Provider>
  )
}

export function useChat() {
  const context = useContext(ChatContext)
  if (context === undefined) {
    throw new Error("useChat must be used within a ChatProvider")
  }
  return context
}
